import { useState } from "react";
import { Plus, Edit, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../ui/collapsible";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem, DragHandle } from "./SortableItem";
import { CateringMenuCategory, CateringMenuItem } from "../../../../hooks/useCateringMenus";

interface AdminCategoryCardProps {
  category: CateringMenuCategory;
  onEditCategory: () => void;
  onEditItem: (item: CateringMenuItem) => void;
  onAddItem: (categoryId: string) => void;
  onChangeSort: (product: any, type: string, categoryID:string) => void;
  onRefetch: () => void;
}

export function AdminCategoryCard({ category, onEditCategory, onEditItem, onAddItem, onChangeSort, onRefetch }: AdminCategoryCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);


  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const getSelectionLabel = () => {
    if (category.selection_type === 'all_included') return 'All Included';
    if (category.max_selection === 1) return 'Choose 1';
    return `Choose ${category.min_selection || 1}-${category.max_selection || category.items.length}`;
  };

  const handleItemDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = category.items.findIndex(i => i.id === active.id);
    const newIndex = category.items.findIndex(i => i.id === over.id);
    
    const newItems = [...category.items];
    const [moved] = newItems.splice(oldIndex, 1);
    newItems.splice(newIndex, 0, moved);
    let updatedMenus = newItems.map((m, index: number) => ({ ...m, display_order: index + 1 }));
    onChangeSort(updatedMenus, 'sort', category.id);
  };

  return (
    <div className="border rounded-lg p-3 bg-muted/30">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DragHandle />
            <div>
              <span className="font-medium">{category.name}</span>
              <Badge variant="outline" className="text-xs">{getSelectionLabel()}</Badge>
              {category.is_required && <Badge variant="destructive" className="text-xs">Required</Badge>}
            </div>
            <p className="text-xs text-muted-foreground">{category.items.length} items</p>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onEditCategory}>
              <Edit className="h-3 w-3" />
            </Button>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm">
                {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>

        <CollapsibleContent>
          <div className="mt-3 pt-3 border-t space-y-2">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleItemDragEnd}>
              <SortableContext items={category.items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                {category.items.map((item) => (
                  <SortableItem key={item.id} id={item.id}>
                    <div className="flex items-center justify-between py-2 px-3 bg-background rounded-md">
                      <div className="flex items-center gap-3">
                        <DragHandle />
                        {item.image_url && (
                          <img src={item.image_url} alt={item.name} className="w-8 h-8 rounded object-cover" />
                        )}
                        <div>
                          <span className="text-sm font-medium">{item.name}</span>
                          <div className="flex items-center gap-2">
                            {item.is_default && <Badge variant="secondary" className="text-xs">Default</Badge>}
                            {item.price_per_pax && item.price_per_pax > 0 && (
                              <span className="text-xs text-accent">+${item.price_per_pax.toFixed(2)}/pax</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => onEditItem(item)}>
                        <Edit className="h-3 w-3" />
                      </Button>
                    </div>
                  </SortableItem>
                ))}
              </SortableContext>
            </DndContext>
            <Button type="button" variant="outline" size="sm" className="w-full mt-2 gap-1" onClick={() => onAddItem(category.id)}>
              <Plus className="h-3 w-3" />
              Add Item
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
